import React, { useRef, useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { motion } from 'framer-motion';

const OtpInput = ({ value, onChange, error, label }) => {
  const [otp, setOtp] = useState(['', '', '', '']);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    // Sync with external value
    if (value) {
      const valueArray = value.split('').slice(0, 4);
      const newOtp = ['', '', '', ''];
      valueArray.forEach((char, index) => {
        if (index < 4) {
          newOtp[index] = char;
        }
      });
      setOtp(newOtp);
    } else if (value === '') {
      setOtp(['', '', '', '']);
    }
  }, [value]);

  const handleChange = (index, newValue) => {
    // Only allow numbers
    if (newValue && !/^\d$/.test(newValue)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = newValue;
    setOtp(newOtp);

    // Call onChange with the combined OTP string
    const otpString = newOtp.join('');
    if (onChange) {
      onChange({
        target: {
          name: 'otp',
          value: otpString,
        },
      });
    }

    // Auto-focus next input
    if (newValue && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    // Handle paste
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const pastedOtp = text.replace(/\D/g, '').slice(0, 4);
        const newOtp = [...otp];
        pastedOtp.split('').forEach((char, i) => {
          if (index + i < 4) {
            newOtp[index + i] = char;
          }
        });
        setOtp(newOtp);
        const otpString = newOtp.join('');
        if (onChange) {
          onChange({
            target: {
              name: 'otp',
              value: otpString,
            },
          });
        }
        // Focus the last filled input or the last input
        const lastFilledIndex = Math.min(index + pastedOtp.length - 1, 3);
        inputRefs[lastFilledIndex].current?.focus();
      });
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      <div className="flex gap-3 justify-center items-center">
        {otp.map((digit, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
          >
            <TextField
              inputRef={inputRefs[index]}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  padding: '12px',
                },
              }}
              error={error && otp.join('').length !== 4}
              sx={{
                width: '64px',
                height: '64px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    borderWidth: error && otp.join('').length !== 4 ? '2px' : '1px',
                    borderColor: error && otp.join('').length !== 4 ? '#ef4444' : '#d1d5db',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    '& fieldset': {
                      borderColor: '#0B469D',
                    },
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0 8px 25px rgba(11, 70, 157, 0.25)',
                    '& fieldset': {
                      borderColor: '#0B469D',
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                },
              }}
            />
          </motion.div>
        ))}
      </div>
      {error && otp.join('').length !== 4 && (
        <p className="text-red-500 text-sm mt-2 text-center">
          Please enter 4 digit OTP
        </p>
      )}
    </div>
  );
};

export default OtpInput;
